const editList = require('../editList/editList');

// attachmentsList: an editList whose rows each hold a single `file` component.
// The only structural add-button is "Attach". Clicking it must, in one user
// gesture: add a new (initially hidden) row, then open the OS file dialog for
// that row's file input. The row is revealed once a file is chosen, or dropped
// again if the dialog is dismissed with no selection.
//
// Multi-file: the dialog is opened with `multiple` so the user can pick several
// files at once. The first picked file stays on the row that opened the dialog;
// every additional file gets its own freshly-created row. Splitting one OS
// selection across several single-file inputs requires programmatically
// assigning a one-file FileList to each input (via DataTransfer). That is
// feature-detected (canAssignFiles) — where it isn't supported (e.g. older iOS
// Safari < 14.5) the component degrades to single-file-per-dialog, so it stays
// functional on every platform.
const attachmentsList = Object.assign({}, editList, {
    // Allow picking several files in one dialog (each becomes its own row).
    // Read from data-zit-allow-multiple when present; defaults on.
    allowMultiple: true,

    // Feature-detect once whether a FileList can be built and assigned to an
    // input — the only standards-compliant way to move a picked File into a
    // different single-file input. Without it, multi-select can't be split
    // into per-row inputs, so we fall back to single-file selection.
    canAssignFiles: function () {
        try {
            const probe = document.createElement('input');
            probe.type = 'file';
            const dt = new DataTransfer();
            probe.files = dt.files;
            return probe.files !== null;
        } catch (e) {
            return false;
        }
    },

    add: function (afterIndex) {
        let countBefore = this.listItems.length;

        // Let editList build + insert the row and init its components (the file
        // component's own change handler is wired here).
        editList.add.call(this, afterIndex);

        // editList.add bails without adding when maxEntries is reached — nothing
        // to attach in that case.
        if (this.listItems.length <= countBefore) {
            return;
        }

        let newListItem = this.listItems[this.listItems.length - 1];
        let rowElement = newListItem.element;
        let fileInput = rowElement.querySelector('input[type="file"]');

        // No file input (misconfigured row) — leave the row visible as a
        // graceful fallback rather than opening a dialog that can't exist.
        if (fileInput === null) {
            return;
        }

        // Only offer multi-select when we can actually split the result into
        // one file per row (see canAssignFiles); otherwise stay single-file.
        let multiple = this.allowMultiple && this.canAssignFiles();
        fileInput.multiple = multiple;

        // Keep the row hidden until a file is actually chosen.
        this.hm.helper.hideElement(rowElement);

        let settled = false;

        // once: the distribution runs for the single real user selection. Making
        // it a one-time listener also means the synthetic 'change' events fired
        // by setRowFile() below (to refresh each row's file display) don't
        // re-enter this handler — a `once` listener is removed before it runs.
        fileInput.addEventListener('change', () => {
            let files = fileInput.files;

            if (!files || files.length === 0) {
                return;
            }

            settled = true;
            this.hm.helper.showElement(rowElement);

            if (multiple && files.length > 1) {
                // Files 2..N each get their own row; the first stays here.
                let extras = Array.prototype.slice.call(files, 1);

                // Reduce this row's input to just its own (first) file so it
                // doesn't also submit the others.
                this.setRowFile(fileInput, files[0]);

                for (let i = 0; i < extras.length; i++) {
                    if (this.addRowForFile(extras[i]) === false) {
                        break; // maxEntries reached (message already shown once)
                    }
                }
            } else {
                // Single file settled here — don't leave the input multi-capable
                // for a later manual re-pick on this same row.
                fileInput.multiple = false;
            }
        }, { once: true });

        // Modern browsers fire 'cancel' on the file input when its OS dialog is
        // dismissed with no selection. Drop the still-empty pending row (it has
        // no saved id yet, so editList.remove just detaches it, no delete flag).
        fileInput.addEventListener('cancel', () => {
            if (!settled && !fileInput.value) {
                this.remove(newListItem.index);
            }
        });

        // Open the OS file dialog straight away. This runs synchronously inside
        // the Attach button's click handler (editList wires add() to the click),
        // so the programmatic .click() is still covered by the user gesture.
        fileInput.click();
    },

    // Assign a single already-picked File to a file input (replacing whatever it
    // holds) and notify its file component so the filename display + validation
    // update. Normalises the input back to single-file.
    setRowFile: function (fileInput, fileObj) {
        let dt = new DataTransfer();
        dt.items.add(fileObj);
        fileInput.files = dt.files;
        fileInput.multiple = false;

        // Let the row's own file component react (updates its filename display)
        // and run validation. Our add() change listener is `once` and already
        // consumed, so this does not re-enter the distribution logic.
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    },

    // Create a fresh row (NOT via this.add, which would open another dialog) and
    // drop a single already-picked File into its file input. Returns false when
    // no row could be added (e.g. maxEntries reached) so the caller can stop.
    addRowForFile: function (fileObj) {
        let countBefore = this.listItems.length;

        editList.add.call(this, -1);

        if (this.listItems.length <= countBefore) {
            return false;
        }

        let listItem = this.listItems[this.listItems.length - 1];
        let fileInput = listItem.element.querySelector('input[type="file"]');

        if (fileInput === null) {
            return false;
        }

        this.setRowFile(fileInput, fileObj);
        this.hm.helper.showElement(listItem.element);

        return true;
    }
});

module.exports = attachmentsList;
